<template>
  <a-modal
    v-model:visible="show"
    unmount-on-close
    esc-to-close
    :mask-closable="false"
    title="添加职位协作人"
    title-align="start"
    width="640px"
    ok-text="添加职位协作人"
    cancel-text="取消"
    @beforeOk="onBeforeOk"
    @ok="onOk"
    @cancel="onCancel"
  >
    <a-add-someone
      ref="addSomeoneRef"
      :default-selected-ids="defaultSelectedIds"
      :list-data="listData"
      :job="job"
      :loading="loading"
      :default-avatar-type="defaultAvatarType"
      :max="selectedLimit?.max"
      :auto-max-length="autoMaxLength"
    >
    </a-add-someone>
  </a-modal>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, type PropType } from 'vue';
import type { IUser, IJob } from './types';

export default defineComponent({
  name: 'AddPositionCollaborator',
  props: {
    /**
     * @zh 获取数据的方法
     * @en the method to get list data
     */
    getData: {
      type: Function as PropType<() => Promise<IUser[]>>,
      default: () => Promise.resolve([]),
    },
    /**
     * @zh 默认选中的项
     * @en default selected items
     */
    defaultSelectedIds: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    /**
     * @zh 职位相关文案
     * @en text of job
     */
    job: {
      type: Object as PropType<IJob>,
      default: () => ({
        id: -1,
        title: '岗位名称',
        nameTitle: '职位负责人',
        name: '某某某',
      }),
    },
    /**
     * @zh 弹窗是否显示
     * @en visible of modal
     */
    visible: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    /**
     * @zh 列表默认头像类型
     * @en default avatar type of list
     */
    defaultAvatarType: {
      type: String as PropType<'dark' | 'light' | 'prism-light'>,
      default: 'prism-light',
    },
    /**
     * @zh 选择数量限制
     * @en count limit of selected
     */
    selectedLimit: {
      type: Object as PropType<{ max?: number; min?: number }>,
      default: () => ({ max: 50, min: 0 }),
    },
    /**
     * @zh 校验选择数量限制的时机
     * @en time of validate count limit of selected
     */
    validateLimit: {
      type: String as PropType<'checked' | 'submit' | 'both'>,
      default: 'both',
    },
    /**
     * @zh 自动判断数据长度和最多选中的数量
     * @en automatically determine data length and maximum selected quantity
     */
    autoMaxLength: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
  },
  emits: ['ok', 'cancel', 'countLimited', 'update:visible'],
  setup(props, { emit }) {
    const listData = ref<IUser[]>([]);
    const loading = ref(false);
    const addSomeoneRef = ref<any>();

    const show = computed({
      get() {
        return props.visible;
      },
      set(_show: boolean) {
        emit('update:visible', _show);
      },
    });

    const validateCountLimit = () => {
      const selected = addSomeoneRef.value?.getSelected?.();
      const selectedIds = addSomeoneRef.value?.getSelectedIds?.();
      const data = {
        selected,
        selectedIds,
      };
      const maxLimit = props.selectedLimit?.max || 10;
      const minLimit = props.selectedLimit?.min || 0;
      if (maxLimit && selectedIds.length > maxLimit) {
        emit('countLimited', {
          ...data,
          limitError: 'max',
        });
        return false;
      }
      if (minLimit && selectedIds.length < minLimit) {
        emit('countLimited', {
          ...data,
          limitError: 'min',
        });
        return false;
      }
      return true;
    };

    const onBeforeOk = () => {
      return validateCountLimit();
    };

    const onOk = () => {
      const selected = addSomeoneRef.value?.getSelected?.();
      const selectedIds = addSomeoneRef.value?.getSelectedIds?.();
      const data = {
        selected,
        selectedIds,
      };
      emit('ok', data);
      emit('update:visible', false);
    };

    const onCancel = () => {
      const selected = addSomeoneRef.value?.getSelected?.();
      const selectedIds = addSomeoneRef.value?.getSelectedIds?.();
      const data = {
        selected,
        selectedIds,
      };
      emit('cancel', data);
      emit('update:visible', false);
    };

    onMounted(async () => {
      loading.value = true;
      listData.value = (await props.getData?.()) || ([] as IUser[]);
      loading.value = false;
    });

    return {
      addSomeoneRef,
      loading,
      onOk,
      onCancel,
      onBeforeOk,
      listData,
      show,
    };
  },
});
</script>

<style scoped></style>
